// cfoArExcelExport — the reusable A/R table → Excel export helper for CFO Core.
// Extracted from the legacy A/R Review / Collectability panels so the native
// `/cfo/ar` page can offer the same one-click table downloads without embedding
// the old dialog. Builds a single-sheet SpreadsheetWorkbookSnapshot (title +
// meta + header + rows) and ships it through `workerClient.export_excel_snapshot`.

import {
  SPREADSHEET_ENGINE_PROTOCOL_VERSION,
  buildSheetChunkId,
  buildSheetChunkKey,
  buildSpreadsheetStyleFromFormat,
  type SpreadsheetCellRecord,
  type SpreadsheetCellValue,
  type SpreadsheetWorkbookSnapshot,
} from '../../../protocol/spreadsheetEngineProtocol'

export type CfoArExcelCell = string | number | boolean | null | undefined

export type CfoArExcelColumn<T> = {
  header: string
  value: (row: T) => CfoArExcelCell
  widthPx?: number
  format?: 'currency' | 'number' | 'days'
}

/**
 * Minimal worker surface needed for the export. Intentionally loose so any
 * AppWorkerClient (whose `export_excel_snapshot` carries the full protocol
 * payload/response types) is assignable here without coupling.
 */
export type CfoArExcelWorkerClient = {
  export_excel_snapshot?: (request: any) => Promise<any>
}

export const safeCfoArExcelFileSegment = (raw: string): string => (
  String(raw || '')
    .replace(/[\\/:*?"<>|]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 90)
)

export function downloadBase64Workbook(args: { fileName: string; contentType?: string | null; packageBase64: string }): void {
  if (typeof document === 'undefined') return
  const binary = atob(args.packageBase64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
  const blob = new Blob([bytes], { type: args.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = args.fileName || 'cfo-core-ar-export.xlsx'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const cfoArExcelCellValue = (value: CfoArExcelCell): SpreadsheetCellValue => {
  if (typeof value === 'number' && Number.isFinite(value)) return { kind: 'number', value }
  if (typeof value === 'boolean') return { kind: 'boolean', value }
  if (typeof value === 'string') return { kind: 'text', value }
  return { kind: 'blank' }
}

const cfoArExcelRawInput = (value: CfoArExcelCell): string => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'string') return value
  return ''
}

const cfoArExcelCell = (value: CfoArExcelCell, styleId?: string | null): SpreadsheetCellRecord => ({
  raw_input: cfoArExcelRawInput(value),
  computed_value: cfoArExcelCellValue(value),
  style_id: styleId || null,
  revision: 1,
})

export function buildCfoArTableExcelSnapshot<T>(args: {
  title: string
  sheetName: string
  columns: CfoArExcelColumn<T>[]
  rows: T[]
  meta?: string | null
}): SpreadsheetWorkbookSnapshot {
  const sheetId = 'sheet_1'
  const styles: Record<string, Record<string, unknown>> = {}
  const addStyle = (id: string, format: Record<string, unknown>): string => {
    const built = buildSpreadsheetStyleFromFormat(format)
    if (built.style) styles[id] = built.style
    return id
  }
  addStyle('ar_title', { bold: true, fontSize: 15, textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle('ar_meta', { fontSize: 10, textColor: '#64748B', align: 'left', verticalAlign: 'middle' })
  addStyle('ar_header', { bold: true, textColor: '#334155', backgroundColor: '#F8FAFC', align: 'left', verticalAlign: 'middle', border: { bottom: { style: 'thin', color: '#CBD5E1' } } })
  addStyle('ar_text', { textColor: '#0F172A', align: 'left', verticalAlign: 'middle' })
  addStyle('ar_number', { numberFormat: { formatCode: '#,##0.00;(#,##0.00);-' }, align: 'right', verticalAlign: 'middle' })
  addStyle('ar_currency', { numberFormat: { formatCode: '$#,##0.00;($#,##0.00);-' }, align: 'right', verticalAlign: 'middle' })
  addStyle('ar_days', { numberFormat: { formatCode: '0 "days";-0 "days";-' }, align: 'right', verticalAlign: 'middle' })

  const rows: CfoArExcelCell[][] = [
    [args.title],
    [args.meta || `Exported ${new Date().toLocaleString()}`],
    [],
    args.columns.map((column) => column.header),
    ...args.rows.map((row) => args.columns.map((column) => column.value(row))),
  ]
  const chunks: Record<string, Record<string, SpreadsheetCellRecord>> = {}
  rows.forEach((rowValues, row) => {
    rowValues.forEach((value, col) => {
      if (value == null || value === '') return
      const chunkId = buildSheetChunkId(buildSheetChunkKey(sheetId, row, col))
      chunks[chunkId] ??= {}
      const column = args.columns[col]
      const styleId = row === 0
        ? 'ar_title'
        : row === 1
          ? 'ar_meta'
          : row === 3
            ? 'ar_header'
            : column?.format === 'currency'
              ? 'ar_currency'
              : column?.format === 'days'
                ? 'ar_days'
                : column?.format === 'number'
                  ? 'ar_number'
                  : 'ar_text'
      chunks[chunkId][`${row}:${col}`] = cfoArExcelCell(value, styleId)
    })
  })
  const colCount = Math.max(1, args.columns.length)
  const rowCount = Math.max(rows.length, 5)
  return {
    protocol_version: SPREADSHEET_ENGINE_PROTOCOL_VERSION,
    workbook_id: `cfo_core_ar_export_${Date.now().toString(36)}`,
    workbook_revision: 1,
    workbook_properties: { date_1904: false },
    document_properties: {
      title: args.title,
      creator: 'FuseGrid CFO Core',
      created: new Date().toISOString(),
      modified: new Date().toISOString(),
    },
    sheets: [{
      sheet_id: sheetId,
      name: String(args.sheetName || 'A/R Review').slice(0, 31),
      row_count: rowCount,
      col_count: colCount,
      chunks,
      column_metadata: Object.fromEntries(args.columns.map((column, index) => [String(index), { width_px: column.widthPx || 120 }])),
      frozen_panes: { rows: 4, cols: 0 },
      merge_ranges: {
        title: { sheet_id: sheetId, start_row: 0, start_col: 0, end_row: 0, end_col: colCount - 1 },
        meta: { sheet_id: sheetId, start_row: 1, start_col: 0, end_row: 1, end_col: colCount - 1 },
      },
      auto_filter: {
        range: { sheet_id: sheetId, start_row: 3, start_col: 0, end_row: Math.max(3, rowCount - 1), end_col: colCount - 1 },
      },
    }],
    sheet_order: [sheetId],
    named_ranges: {},
    validation_rules: {},
    styles,
    conditional_formats: {},
    sparkline_groups: {},
    derived_artifacts: {},
    external_sources: {},
  }
}

/**
 * Build + ship a single-table A/R Excel export, then trigger the browser
 * download. `setStatus` receives human-readable progress/failure text. Guards
 * cleanly when the worker (or its `export_excel_snapshot`) is unavailable.
 */
export async function downloadCfoArTableExcel<T>(args: {
  workerClient: CfoArExcelWorkerClient | null | undefined
  setStatus?: (status: string) => void
  title: string
  sheetName: string
  fileSegment: string
  rows: T[]
  columns: CfoArExcelColumn<T>[]
  meta?: string | null
}): Promise<void> {
  const setStatus = args.setStatus || (() => {})
  if (!args.workerClient?.export_excel_snapshot) {
    setStatus('Excel export is not available in this session.')
    return
  }
  setStatus(`Exporting ${args.title}...`)
  try {
    const fileBase = safeCfoArExcelFileSegment(`CFO Core ${args.fileSegment} ${new Date().toISOString().slice(0, 10)}`) || 'CFO Core AR'
    const fileName = `${fileBase}.xlsx`
    const snapshot = buildCfoArTableExcelSnapshot({
      title: args.title,
      sheetName: args.sheetName,
      rows: args.rows,
      columns: args.columns,
      meta: args.meta,
    })
    const response = await args.workerClient.export_excel_snapshot({
      format: 'xlsx',
      file_name: fileName,
      include_calculated_values: true,
      snapshot,
      actor_client_id: 'cfo_core_ar_table_export',
      created_at_ms: Date.now(),
    })
    downloadBase64Workbook({
      fileName: response.file_name || fileName,
      contentType: response.content_type,
      packageBase64: response.package_base64,
    })
    setStatus(`Exported ${response.file_name || fileName}.`)
  } catch (error: any) {
    setStatus(`Excel export failed: ${error?.message || String(error)}`)
  }
}
